// UI strings of Ääntämisvalmentaja's screens (docs/design/AANTAMISVALMENTAJA.md "UI"; Axel's direction of
// 2026-10-11, "Tikapuut + Partituuri score"): the hub, a sound's guide page, the live rung screen (the rung rail,
// the score's lanes and readouts, the take dots, the verdicts, the one correction per take), the voice and free
// mode, the calibration, the microphone check (with the PC guide for a headset), the sources and the 60-second
// "Miten kuvaa luetaan" tour. The per-sound teaching text is coachGuide, the mic's states coachLive, the mouth
// pictures' words coachMouth. Style: docs/FINNISH-STYLE.md (sinä, no jargon, no intro text). en: the same shape.
import type { CoachFixId, Verdict } from '@valyrio/engine/coach/contract';
import { defineArea } from '../area';

const fiNum = (n: number, d = 0) => n.toLocaleString('fi-FI', { minimumFractionDigits: d, maximumFractionDigits: d });
const enNum = (n: number, d = 0) => n.toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
/** A Finnish date "11.10." */
const fiDay = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${Number(d)}.${Number(m)}.`;
};
const enDay = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${Number(d)}/${Number(m)}`;
};
/** A readout segment: "sulku: sinä 0 ms, Peterson 62 ms"; '' when neither side has a number. */
const fiSeg = (name: string, you: number | null, his: number | null, f: (x: number) => string) =>
  you === null && his === null ? '' : `${name}: ${[you === null ? '' : `sinä ${f(you)}`, his === null ? '' : `Peterson ${f(his)}`].filter(Boolean).join(', ')}`;
const enSeg = (name: string, you: number | null, his: number | null, f: (x: number) => string) =>
  you === null && his === null ? '' : `${name}: ${[you === null ? '' : `you ${f(you)}`, his === null ? '' : `Peterson ${f(his)}`].filter(Boolean).join(', ')}`;

export type LaneLabel = 'buzz' | 'hiss' | 'place' | 'beats' | 'gap' | 'level' | 'pitch';

export const coachUiFi = {
  hub: {
    sounds: 'Äänteet',
    tools: 'Muut',
    voice: 'Äänenkäyttö ja vapaa harjoitus',
    voiceNote: 'Sävel, paino ja ääniala missä tahansa lauseessa',
    voiceName: 'Äänenkäyttö',
    calibration: 'Oman äänen kalibrointi',
    calNone: 'Tekemättä tällä laitteella',
    calDone: (day: string) => `Tehty ${fiDay(day)}`,
    calOther: 'Toisen laitteen kalibrointi: tee uusi tällä laitteella',
    calOld: 'Tee uudelleen: mittarit ovat muuttuneet',
    diagnostics: 'Mikrofonin tarkistus',
    diagNote: 'Myös PC:n kuulokemikrofoni',
    tour: 'Miten kuvaa luetaan',
    credits: 'Lähteet',
    notStarted: 'Ei aloitettu',
    done: 'Kaikki askeleet läpi',
    /** "Askel 3/8 · Sanan alussa" */
    rung: (n: number, total: number, name: string) => `Askel ${n}/${total} · ${name}`,
    next: 'Seuraavaksi',
  },
  rule: {
    /** The pass rule in one plain sentence, from the engine's numbers. */
    text: (rate: number, days: number, takes: number, reopen: number | null) =>
      `Askel menee läpi, kun ${days} eri päivänä vähintään ${rate} % toistoista on osumia ja arvioituja toistoja on joka päivä vähintään ${takes}.` +
      (reopen !== null ? ` Jos myöhempi päivä jää alle ${reopen} %:n, askel avautuu uudelleen.` : ''),
    /** A rule that counts hits (no share): "Läpi: 4 onnistunutta toistoa 2 eri päivänä." */
    textHits: (pass: string) => `Läpi: ${pass}.`,
    unsure: 'Epävarma toisto ei ole osuma eikä ohi.',
    unsureDay: (pct: number) => `Päivä ei kelpaa, jos yli ${pct} % sen toistoista on epävarmoja.`,
    /** "tänään 7/10 · 86 %" */
    today: (judged: number, need: number, rate: number | null) => (rate === null ? `tänään 0/${need}` : `tänään ${Math.min(judged, need)}/${need} · ${rate} %`),
    todayLabel: (judged: number, need: number, rate: number | null) =>
      rate === null
        ? `Tänään ei vielä arvioituja toistoja, tarvitaan ${need}.`
        : `Tänään ${judged === 1 ? '1 arvioitu toisto' : `${judged} arvioitua toistoa`}, osumia ${rate} %. Päivään tarvitaan ${need}.`,
    /** "päivät 2/5" */
    days: (n: number, need: number) => `päivät ${n}/${need}`,
    daysLabel: (n: number, need: number) => `Läpäistyjä päiviä ${n}, tarvitaan ${need}.`,
    passed: (day: string) => `läpi ${fiDay(day)}`,
    passedShort: 'läpi',
    reopened: 'avautui uudelleen',
    now: 'nyt',
    ahead: 'tulossa',
    listen: 'kuuntele',
  },
  guide: {
    practise: 'Harjoittele',
    continueAt: (n: number) => `Jatka askeleesta ${n}`,
    again: 'Harjoittele uudelleen',
    place: 'Kieli, huulet ja ääni',
    start: 'Lähde suomesta',
    test: 'Tunnustele',
    error: 'Tavallinen virhe',
    variants: 'Kelpaa myös',
    words: 'Petersonin sanoja',
    ladder: 'Tikapuut',
    slow: '0,75×',
    normal: '1×',
    playSlow: (w: string) => `0,75×: kuuntele ${w} hitaasti`,
    playNormal: (w: string) => `1×: kuuntele ${w}`,
    views: 'Kuvat',
    poses: 'Asennot',
    noWords: 'Petersonin äänitteissä ei ole tämän äänteen sanoja.',
    /** The phase picker: a pose by its part or its sound (others show the letter). */
    poseNames: {
      'j-closure': 'sulku',
      'j-release': 'avaus',
      'j-hiss': 'sihinä',
      sh: 'š',
      zh: 'ž',
      ny: 'ñ',
      x: 'kahvin h',
      'fi-j': 'suomen j',
      'fi-v': 'suomen v',
      voice: 'puheääni',
      'voice-high': 'korkea',
      'voice-low': 'matala',
    } as Record<string, string>,
  },
  live: {
    allRungs: 'Kaikki askeleet',
    ladder: (sound: string) => `Tikapuut: ${sound}`,
    peterson: 'Peterson',
    slow: '0,75×',
    normal: '1×',
    playSlow: 'Peterson 0,75×: kuuntele hitaasti',
    playNormal: 'Peterson 1×: kuuntele normaalilla nopeudella',
    own: 'Oma otto',
    ownLabel: 'Oma otto: kuuntele viimeisin ottosi',
    mic: { open: 'Avaa mikrofoni', pause: 'Tauko', continue: 'Jatka', starting: 'Avataan…' },
    keys: { space: 'Välilyönti' },
    listening: 'Kuuntelen.',
    idle: 'Avaa mikrofoni, niin kuuntelen.',
    sayIt: 'Sano se, kun olet valmis.',
    petersonPlays: 'Peterson puhuu.',
    hearFirst: 'Kuuntele ensin hitaasti, sitten normaalisti.',
    heard: (slow: boolean, normal: boolean) => `${slow ? '✓' : '·'} hitaasti  ${normal ? '✓' : '·'} normaalisti`,
    hearDone: 'Kuunneltu. Seuraava askel on auki.',
    nextRung: 'Seuraava askel',
    prevRung: 'Edellinen askel',
    passedRung: 'Askel meni läpi.',
    verdict: { hit: 'Osuma', near: 'Melkein', miss: 'Ei vielä', unsure: 'En ole varma' } satisfies Record<Verdict, string>,
    /** "Kuulostaa ž:ltä" — the model's strongest competitor. */
    soundsLike: (x: string) => `Kuulostaa ${x}:ltä`,
    model: (peterson: number | null, you: number) => (peterson === null ? `sinä ${fiNum(you, 2)}` : `Peterson ${fiNum(peterson, 2)} · sinä ${fiNum(you, 2)}`),
    modelLabel: (peterson: number | null, you: number) =>
      peterson === null
        ? `Tunnistuksen mukaan osuit ${fiNum(you * 100)} %:n varmuudella.`
        : `Tunnistus: Peterson ${fiNum(peterson * 100)} %, sinä ${fiNum(you * 100)} %.`,
    notCounted: 'Ei lasketa ohi menneeksi.',
    /** The per-take summary for screen readers. */
    summary: (n: number, verdict: string, fix: string) => `Otto ${n}: ${verdict}.${fix ? ` ${fix}` : ''}`,
    take: (n: number) => `otto ${n}`,
    lanes: {
      buzz: 'ääni',
      hiss: 'hankaus',
      place: 'sihinän paikka',
      beats: 'sulku · sihinä',
      gap: 'sulku',
      level: 'voimakkuus',
      pitch: 'sävel',
    } satisfies Record<LaneLabel, string>,
    petersonAbove: 'Peterson',
    youBelow: 'sinä',
    /** Readouts, yours beside Peterson's: "sulku: sinä 0 ms, Peterson 62 ms" (a side without a number is left out). */
    read: {
      closure: (you: number | null, his: number | null) => fiSeg('sulku', you, his, (x) => `${fiNum(x)}\u00a0ms`),
      hiss: (you: number | null, his: number | null) => fiSeg('sihinä', you, his, (x) => `${fiNum(x)}\u00a0ms`),
      voiced: (you: number | null, his: number | null) => fiSeg('ääni päällä', you, his, (x) => `${fiNum(x)}\u00a0%`),
      gap: (you: number | null, his: number | null) => fiSeg('katko', you, his, (x) => `${fiNum(x)}\u00a0dB`),
      taps: (you: number | null, his: number | null) => fiSeg('täryjä', you, his, (x) => fiNum(x)),
      lift: (you: number | null, his: number | null) =>
        fiSeg('nousu', you, his, (x) => `${x >= 0 ? '+' : ''}${fiNum(x, 1)}`) + (you === null && his === null ? '' : ' puolisävelaskelta'),
      place: (you: number | null) => fiSeg('sihinä', you, null, (x) => `${fiNum(x / 1000, 1)}\u00a0kHz`),
    },
    /** The one correction per take (CoachFixId), one plain sentence. */
    fixes: {
      'keep-buzz': 'Pidä ääni päällä äänteen loppuun asti.',
      'add-buzz': 'Anna äänihuulten väristä koko äänteen ajan.',
      'less-buzz': 'Tämä äänne on soinniton: anna äänihuulten levätä.',
      'tip-forward': 'Vie kielen kärkeä eteenpäin hammasvallin taakse, niin sihinä kirkastuu.',
      'tip-back': 'Vie kieltä hieman taaksepäin, niin sihinä tummuu.',
      'add-stop': 'Aloita sululla: kieli kiinni hammasvallissa ennen sihinää.',
      'shorter-hiss': 'Lyhennä sihinää: avaa sulku suoraan lyhyeen š:hen.',
      'no-gap': 'Älä pysäytä ilmaa: kieli lähelle suulakea mutta ei kiinni.',
      'further-back': 'Vie sulku taemmas, kitakielekkeen kohdalle.',
      'one-sound': 'Sano yksi äänne, ei kahta peräkkäin.',
      'more-taps': 'Anna kielen kärjen täristä useamman kerran.',
      'round-lips': 'Pyöristä huulet.',
      'stress-later': 'Paino ei ole ensimmäisellä tavulla: nosta ääntäsi painollisella tavulla.',
      'more-melody': 'Anna äänesi nousta painollisella tavulla.',
      'louder-stress': 'Sano painollinen tavu voimakkaammin.',
    } satisfies Record<CoachFixId, string>,
    /** j at the start of a word: Axel's two habits made vivid (Axel 2026-10-11). */
    jFixes: {
      'add-buzz': 'Kuulostaa tš:ltä: anna äänen alkaa jo sulun aikana ja lyhennä sihinää.',
      'add-stop': 'Kuulostaa ž:ltä: aloita sululla, kieli kiinni hammasvallissa, ja päästä vasta sitten sihinä.',
    } as Partial<Record<CoachFixId, string>>,
    hit: 'Juuri noin.',
    /** Why a take is unsure (SoundEvent.why), the reason in a few words. */
    why: {
      noise: 'Taustalla on ääntä. Mene hiljaisempaan paikkaan tai lähemmäs mikrofonia.',
      clipping: 'Ääni säröytyi. Puhu hiljempaa tai kauempana mikrofonista.',
      'band-limited': 'Mikrofoni ei kuule korkeita ääniä.',
      disabled: 'Tätä mittaria ei vielä käytetä arviointiin.',
      between: 'Tulos jäi rajalle.',
      alignment: 'En löytänyt äännettä sanasta.',
      'after-pause': 'En löytänyt äännettä sanasta.',
      disagree: 'Mittari ja tunnistus olivat eri mieltä.',
      measurement: 'Tämä on mittaus, ei arvio.',
      'onset-cut': 'Sana alkoi heti, kun kuuntelin. Ole hetki hiljaa ennen sanaa.',
      'hiss-unclear': 'Sihinä jäi epäselväksi.',
      'hiss-apart': 'Ääni alkoi vasta kauan sihinän jälkeen.',
      cluster: 'Viereinen äänne peitti tämän äänteen.',
      'no-vowel': 'Äänteen vierestä puuttui vokaali.',
      other: 'Äännettä ei voitu mitata varmasti.',
    } as Record<string, string>,
    bandLimitedWell: 'Tämä mikrofoni ei kuule sihinää. Vaihda puhelimen tai PC:n omaan mikrofoniin.',
    /** The take gate (2026-10-12): a breath, a click or a bump was no take; said quietly, never a miss. */
    noSpeech: 'En kuullut puhetta, joten ottoa ei laskettu.',
    /** Narrowband mode (2026-10-12): the mic's measured band top (Hz), or null when only its kind or rate tells. */
    narrowband: (hz: number | null) =>
      hz
        ? `Tämä mikrofoni kuulee vain noin ${fiNum(hz / 1000)} kHz:iin asti: sihinän paikkaa ei mitata, muu toimii. Puhelimen mikrofoni kuulee kaiken.`
        : 'Tämä mikrofoni ei kuule korkeimpia ääniä: sihinän paikkaa ei mitata, muu toimii. Puhelimen mikrofoni kuulee kaiken.',
    deniedWell: 'Salli mikrofonin käyttö selaimen asetuksissa.',
    noCalibration: 'Kalibroi äänesi, niin mittarit käyttävät vertailukohtana sinun omia äänteitäsi.',
    calibrate: 'Kalibroi',
    lips: {
      on: 'Huulikamera',
      off: 'Huulikamera pois',
      note: 'Kuva ei lähde puhelimesta.',
      you: 'Sinun huulesi (kamera)',
      youText: (widthMm: number, gapMm: number) =>
        `Huulesi kameran mukaan: leveys noin ${fiNum(widthMm)} mm, ${gapMm < 1 ? 'huulet kiinni' : `huulten väli noin ${fiNum(gapMm)} mm`}.`,
    },
    views: { side: 'Sivulta', palate: 'Alta', lips: 'Edestä' },
    showBig: (v: string) => `Näytä isona: ${v}`,
    modelLoading: 'Tunnistus latautuu…',
    help: 'Miten kuvaa luetaan',
  },
  tour: {
    title: 'Miten kuvaa luetaan',
    steps: [
      'Viivan yläpuolella on Peterson, alapuolella sinä. Kirjaimet näyttävät, missä kohdassa sanaa ollaan.',
      'Sininen on sinun äänesi. Se piirtyy sitä mukaa kuin puhut.',
      'Oljenkeltainen merkitsee kohdan, jossa ääni meni tavoitteen ohi. Sen alla on yksi neuvo.',
      'Harmaa katkoviiva tarkoittaa, etten ole varma. Sitä ei lasketa ohi menneeksi.',
      'Suun kuva näyttää tavoitteen. Katkoviivainen ääriviiva on arvio äänestäsi, ei kuva suustasi.',
      'Pisteet ovat tämän päivän toistot. Tikapuista näet, monenako päivänä olet jo päässyt askeleen läpi.',
    ],
    next: 'Seuraava',
    done: 'Valmis',
    skip: 'Ohita',
    of: (n: number, total: number) => `${n}/${total}`,
  },
  calibration: {
    intro: 'Noin 20 sekuntia: hiljaisuus, pitkä s, z ja š kukin kolmesti sekä pitkä a kahdesti.',
    start: 'Aloita',
    steps: {
      silence: 'Ole hiljaa.',
      s: 'Sano pitkä s.',
      z: 'Sano pitkä z.',
      sh: 'Sano pitkä š, kuten sanassa šakki.',
      vowel: 'Sano pitkä a omalla puheäänelläsi.',
    },
    rest: 'Hengitä.',
    round: (n: number, total: number) => `${n}/${total}`,
    measuring: 'Lasketaan…',
    save: 'Tallenna',
    again: 'Tee uudelleen',
    saved: 'Tallennettu. Mittarit käyttävät nyt vertailukohtana sinun omia äänteitäsi.',
    ok: 'Kaikki näytti hyvältä.',
    problems: {
      'band-limited': 'Mikrofoni ei kuule korkeita ääniä, joten sihinän paikkaa ei voi mitata. Muut mittarit toimivat.',
      noise: 'Taustamelu on liian kovaa. Mene hiljaisempaan paikkaan.',
      clipping: 'Ääni säröytyi. Puhu hiljempaa tai kauempana mikrofonista.',
      'noise-gate': 'Hiljaisuudessa ei kuulu lainkaan kohinaa: mikrofonin kohinanpoisto tai kohinaportti on päällä.',
      'too-short': 'Jokin äänteistä jäi liian lyhyeksi. Jatka äännettä koko palkin ajan.',
      's-sh-close': 's ja š kuulostivat liian samanlaisilta. Pyöristä huulet š:ssä ja vie kieltä taaksepäin.',
      'z-not-voiced': 'z ei surissut. Anna äänihuulten väristä.',
    } as Record<string, string>,
    keepAnyway: 'Tallenna silti',
    micFirst: 'Avaa mikrofoni ensin.',
  },
  voice: {
    ladder: 'Tikapuut',
    free: 'Vapaa',
    freeHint: 'Sano mikä tahansa lause, myös englanniksi. Sävel on mittaus, ei arvosana.',
    measure: {
      range: (st: number) => `ääniala ${fiNum(st, 1)} puolisävelaskelta`,
      voiced: (s: number) => `ääntä ${fiNum(s, 1)} s`,
      lift: (st: number) => `nousu painollisella tavulla ${st >= 0 ? '+' : ''}${fiNum(st, 1)}`,
      loud: (db: number) => `painollinen tavu ${db >= 0 ? '+' : ''}${fiNum(db, 1)} dB`,
      span: (db: number) => `voimakkuus vaihteli ${fiNum(db)} dB`,
    },
    rangeLabel: 'Ääniala',
    yourLast: 'Viimeisin otto',
    none: 'Sano jotain, niin sävelkäyrä piirtyy.',
  },
  diag: {
    open: 'Avaa mikrofoni',
    close: 'Sulje mikrofoni',
    now: 'Mikrofoni nyt',
    device: 'Laite',
    rate: 'Näytteenottotaajuus',
    processing: 'Äänenkäsittely',
    raw: 'pois päältä',
    on: (what: string) => `päällä: ${what}`,
    ec: 'kaiunpoisto',
    ns: 'kohinanpoisto',
    agc: 'automaattinen voimakkuus',
    band: 'Kuulee',
    bandHz: (hz: number) => `${fiNum(hz)} Hz:iin asti`,
    floor: 'Taustamelu',
    floorDb: (db: number) => `${fiNum(db)} dB`,
    hop: 'Viive',
    hopMs: (ms: number) => `${fiNum(ms, 1)} ms`,
    waiting: 'mitataan…',
    check: {
      h: 'Tarkista, että äänenkäsittely on pois päältä',
      start: 'Aloita tarkistus',
      again: 'Tarkista uudelleen',
      steps: {
        quiet: 'Ole hiljaa 3 sekuntia.',
        normal: 'Lue normaalilla äänellä: «valar morghūlis».',
        loud: 'Lue sama kovempaa.',
        hiss: 'Sano pitkä s.',
      },
      ok: 'Mikrofoni antaa käsittelemätöntä ääntä.',
      found: {
        flags: (what: string) => `Selain ilmoittaa, että ${what} on päällä.`,
        gate: 'Sanojen välissä ei kuulu lainkaan kohinaa: kohinaportti tai kohinanpoisto on päällä.',
        compressor: 'Voimakkuus ei kasvanut, kun puhuit kovempaa: kompressori tai automaattinen voimakkuus on päällä.',
        deesser: 's-äänen sihinä on vaimea: sihinänvaimennin tai kaistanrajaus on päällä.',
        band: (hz: number) => `Mikrofoni kuulee vain ${fiNum(hz)} Hz:iin asti: sihinän paikkaa ei mitata, muu toimii.`,
        noise: 'Taustamelu on kovaa.',
      },
    },
    pc: {
      h: 'Mikrofoni PC:llä',
      lead: 'Valmentaja tarvitsee mikrofonin käsittelemättömän äänen. Kytke nämä pois päältä, niin mittarit näkevät sihinän ja soinnin oikein.',
      steps: [
        'Logitech G HUB: valitse G Pro X ja kytke Blue VO!CE pois päältä. Silloin kohinanpoisto, kompressori, sihinänvaimennin, rajoitin ja kohinaportti ovat kaikki pois päältä.',
        'Windows 11: Asetukset → Järjestelmä → Ääni → Syöte → valitse mikrofoni. Valitse Äänen parannukset -valikosta pois käytöstä. Se poistaa käytöstä myös Windowsin puheen selkeytyksen (Voice Clarity).',
        'Jos koneessa on Windows-studiotehosteet: Asetukset → Bluetooth & laitteet → Kamerat → valitse kamera ja kytke Äänen kohdistus pois päältä. Saman voit tehdä pika-asetusten Studiotehosteet-ruudusta.',
        'Samassa näkymässä: äänenvoimakkuus noin 70–80 ilman vahvistusta, muoto 48 000 Hz.',
        'Käytä Chromea tai Edgeä.',
        'Sulje Discord, NVIDIA Broadcast ja Krisp, tai kytke niiden äänenkäsittely pois päältä.',
      ],
      after: 'Tarkista sitten alla, ettei käsittelyä jäänyt päälle.',
    },
    playback: {
      h: 'Toisto mikrofonin ollessa auki',
      ask: 'Kuulostiko Petersonin äänite samalta, kun mikrofoni oli auki?',
      play: 'Toista näyte',
      same: 'Samalta',
      worse: 'Huonommalta',
      unsure: 'En osaa sanoa',
    },
  },
  credits: {
    model: 'Ääntämisen tunnistus',
    modelText: 'ZIPA-CR-NS-small, lisenssi CC BY-NC-SA 4.0. Malli toimii puhelimessa; ääntä ei lähetetä mihinkään.',
    lips: 'Huulikamera',
    lipsText: 'MediaPipe Face Landmarker, lisenssi Apache 2.0. Kamerakuva käsitellään puhelimessa, eikä sitä tallenneta.',
    peterson: 'Malliäänet',
    petersonText: 'Kaikki valyrian malliäänet ovat David J. Petersonin omia äänitteitä hänen wikistään.',
  },
};

export const coachUiEn: typeof coachUiFi = {
  hub: {
    sounds: 'Sounds',
    tools: 'More',
    voice: 'Voice and free practice',
    voiceNote: 'Pitch, stress and range in any sentence',
    voiceName: 'Voice',
    calibration: 'Calibrating your voice',
    calNone: 'Not done on this device',
    calDone: (day) => `Done ${enDay(day)}`,
    calOther: "Another device's calibration: make a new one on this device",
    calOld: 'Redo it: the meters have changed',
    diagnostics: 'Microphone check',
    diagNote: "Also a PC's headset microphone",
    tour: 'How to read the picture',
    credits: 'Sources',
    notStarted: 'Not started',
    done: 'Every step passed',
    rung: (n, total, name) => `Step ${n}/${total} · ${name}`,
    next: 'Next',
  },
  rule: {
    text: (rate, days, takes, reopen) =>
      `A step passes when at least ${rate} % of the takes hit on ${days} different days, with at least ${takes} judged takes each day.` +
      (reopen !== null ? ` A later day under ${reopen} % opens it again.` : ''),
    textHits: (pass) => `Passes with ${pass}.`,
    unsure: 'An unsure take is neither a hit nor a miss.',
    unsureDay: (pct) => `A day does not count if more than ${pct} % of its takes are unsure.`,
    today: (judged, need, rate) => (rate === null ? `today 0/${need}` : `today ${Math.min(judged, need)}/${need} · ${rate} %`),
    todayLabel: (judged, need, rate) =>
      rate === null ? `No judged takes today yet; ${need} needed.` : `Today ${judged} judged takes, ${rate} % hit. A day needs ${need}.`,
    days: (n, need) => `days ${n}/${need}`,
    daysLabel: (n, need) => `${n} days passed, ${need} needed.`,
    passed: (day) => `passed ${enDay(day)}`,
    passedShort: 'passed',
    reopened: 'opened again',
    now: 'now',
    ahead: 'ahead',
    listen: 'listen',
  },
  guide: {
    practise: 'Practise',
    continueAt: (n) => `Continue at step ${n}`,
    again: 'Practise again',
    place: 'Tongue, lips and voice',
    start: 'Start from Finnish',
    test: 'Feel it',
    error: 'The usual mistake',
    variants: 'Also fine',
    words: "Peterson's words",
    ladder: 'The ladder',
    slow: '0.75×',
    normal: '1×',
    playSlow: (w) => `0.75×: listen to ${w} slowly`,
    playNormal: (w) => `1×: listen to ${w}`,
    views: 'Pictures',
    poses: 'Positions',
    noWords: "Peterson's recordings have no words with this sound.",
    poseNames: {
      'j-closure': 'stop',
      'j-release': 'release',
      'j-hiss': 'hiss',
      sh: 'š',
      zh: 'ž',
      ny: 'ñ',
      x: 'the h of kahvi',
      'fi-j': 'Finnish j',
      'fi-v': 'Finnish v',
      voice: 'speaking voice',
      'voice-high': 'high',
      'voice-low': 'low',
    },
  },
  live: {
    allRungs: 'All steps',
    ladder: (sound) => `Ladder: ${sound}`,
    peterson: 'Peterson',
    slow: '0.75×',
    normal: '1×',
    playSlow: 'Peterson 0.75×: listen slowly',
    playNormal: 'Peterson 1×: listen at normal speed',
    own: 'My take',
    ownLabel: 'My take: listen to your last take',
    mic: { open: 'Open the microphone', pause: 'Pause', continue: 'Continue', starting: 'Opening…' },
    keys: { space: 'Space' },
    listening: 'Listening.',
    idle: 'Open the microphone and I will listen.',
    sayIt: 'Say it when you are ready.',
    petersonPlays: 'Peterson is speaking.',
    hearFirst: 'Listen slowly first, then at normal speed.',
    heard: (slow, normal) => `${slow ? '✓' : '·'} slowly  ${normal ? '✓' : '·'} normal`,
    hearDone: 'Listened. The next step is open.',
    nextRung: 'Next step',
    prevRung: 'Previous step',
    passedRung: 'The step passed.',
    verdict: { hit: 'Hit', near: 'Nearly', miss: 'Not yet', unsure: 'Not sure' },
    soundsLike: (x) => `Sounds like ${x}`,
    model: (peterson, you) => (peterson === null ? `you ${enNum(you, 2)}` : `Peterson ${enNum(peterson, 2)} · you ${enNum(you, 2)}`),
    modelLabel: (peterson, you) =>
      peterson === null
        ? `Recognition: you hit with ${enNum(you * 100)} % certainty.`
        : `Recognition: Peterson ${enNum(peterson * 100)} %, you ${enNum(you * 100)} %.`,
    notCounted: 'Not counted as a miss.',
    summary: (n, verdict, fix) => `Take ${n}: ${verdict}.${fix ? ` ${fix}` : ''}`,
    take: (n) => `take ${n}`,
    lanes: {
      buzz: 'voice',
      hiss: 'friction',
      place: 'hiss place',
      beats: 'stop · hiss',
      gap: 'closure',
      level: 'loudness',
      pitch: 'pitch',
    },
    petersonAbove: 'Peterson',
    youBelow: 'you',
    read: {
      closure: (you, his) => enSeg('stop', you, his, (x) => `${enNum(x)}\u00a0ms`),
      hiss: (you, his) => enSeg('hiss', you, his, (x) => `${enNum(x)}\u00a0ms`),
      voiced: (you, his) => enSeg('voice on', you, his, (x) => `${enNum(x)}\u00a0%`),
      gap: (you, his) => enSeg('dip', you, his, (x) => `${enNum(x)}\u00a0dB`),
      taps: (you, his) => enSeg('taps', you, his, (x) => enNum(x)),
      lift: (you, his) => enSeg('lift', you, his, (x) => `${x >= 0 ? '+' : ''}${enNum(x, 1)}`) + (you === null && his === null ? '' : ' semitones'),
      place: (you) => enSeg('hiss', you, null, (x) => `${enNum(x / 1000, 1)}\u00a0kHz`),
    },
    fixes: {
      'keep-buzz': 'Keep the voice on to the end of the sound.',
      'add-buzz': 'Let the vocal folds buzz through the whole sound.',
      'less-buzz': 'This sound is voiceless: let the vocal folds rest.',
      'tip-forward': 'Move the tongue tip forward behind the ridge to make the hiss brighter.',
      'tip-back': 'Move the tongue back a little to make the hiss darker.',
      'add-stop': 'Start with a stop: tongue closed on the ridge before the hiss.',
      'shorter-hiss': 'Shorten the hiss: open the stop straight into a short š.',
      'no-gap': 'Do not stop the air: tongue close to the palate but not touching.',
      'further-back': 'Move the stop further back, to the uvula.',
      'one-sound': 'Say one sound, not two in a row.',
      'more-taps': 'Let the tongue tip trill a few more times.',
      'round-lips': 'Round your lips.',
      'stress-later': 'The stress is not on the first syllable: raise your voice on the stressed one.',
      'more-melody': 'Let your voice rise on the stressed syllable.',
      'louder-stress': 'Say the stressed syllable louder.',
    },
    jFixes: {
      'add-buzz': 'Sounds like tš: let the voice start during the stop and shorten the hiss.',
      'add-stop': 'Sounds like ž: start with a stop, tongue closed on the ridge, and only then let the hiss out.',
    },
    hit: 'Just like that.',
    why: {
      noise: 'There is noise in the background. Move somewhere quieter or closer to the microphone.',
      clipping: 'The sound distorted. Speak more softly or further from the microphone.',
      'band-limited': 'The microphone does not hear high sounds.',
      disabled: 'This meter is not used for judging yet.',
      between: 'The result fell on the line.',
      alignment: 'I could not find the sound in the word.',
      'after-pause': 'I could not find the sound in the word.',
      disagree: 'The meter and the recognition disagreed.',
      measurement: 'This is a measurement, not a judgement.',
      'onset-cut': 'The word started as soon as I listened. Be quiet for a moment before the word.',
      'hiss-unclear': 'The hiss was unclear.',
      'hiss-apart': 'The voice started long after the hiss.',
      cluster: 'A sound next to it covered this one.',
      'no-vowel': 'There was no vowel next to the sound.',
      other: 'The sound could not be measured for sure.',
    },
    bandLimitedWell: "This microphone does not hear the hiss. Switch to the phone's or the PC's own microphone.",
    noSpeech: 'I heard no speech, so the take was not counted.',
    narrowband: (hz) =>
      hz
        ? `This microphone only reaches about ${enNum(hz / 1000)} kHz: the hiss's place is not measured, everything else works. The phone's microphone hears it all.`
        : "This microphone does not reach the highest sounds: the hiss's place is not measured, everything else works. The phone's microphone hears it all.",
    deniedWell: 'Allow the microphone in the browser settings.',
    noCalibration: 'Calibrate your voice so the meters compare with your own sounds.',
    calibrate: 'Calibrate',
    lips: {
      on: 'Lips camera',
      off: 'Lips camera off',
      note: 'The picture never leaves the phone.',
      you: 'Your lips (camera)',
      youText: (widthMm, gapMm) => `Your lips from the camera: about ${enNum(widthMm)} mm wide, ${gapMm < 1 ? 'closed' : `about ${enNum(gapMm)} mm apart`}.`,
    },
    views: { side: 'Side', palate: 'Below', lips: 'Front' },
    showBig: (v) => `Show large: ${v}`,
    modelLoading: 'Recognition is loading…',
    help: 'How to read the picture',
  },
  tour: {
    title: 'How to read the picture',
    steps: [
      'Above the line is Peterson, below it you. The letters show where in the word you are.',
      'Blue is your voice. It draws as you speak.',
      'Straw marks the spot where the sound went past the target. One tip sits below it.',
      'A grey dotted line means I am not sure. It does not count as a miss.',
      'The mouth picture shows the target. The dotted outline is an estimate from your voice, not a picture of your mouth.',
      "The dots are today's takes. The ladder shows how many days of the step have passed.",
    ],
    next: 'Next',
    done: 'Done',
    skip: 'Skip',
    of: (n, total) => `${n}/${total}`,
  },
  calibration: {
    intro: 'About 20 seconds: silence, a long s, z and š three times each, and a long a twice.',
    start: 'Start',
    steps: {
      silence: 'Be quiet.',
      s: 'Say a long s.',
      z: 'Say a long z.',
      sh: 'Say a long š, as in "shop".',
      vowel: 'Say a long a in your speaking voice.',
    },
    rest: 'Breathe.',
    round: (n, total) => `${n}/${total}`,
    measuring: 'Calculating…',
    save: 'Save',
    again: 'Redo',
    saved: 'Saved. The meters now compare with your own sounds.',
    ok: 'Everything looked fine.',
    problems: {
      'band-limited': 'The microphone does not hear high sounds, so the hiss place cannot be measured. The other meters work.',
      noise: 'The background is too loud. Move somewhere quieter.',
      clipping: 'The sound distorted. Speak more softly or further from the microphone.',
      'noise-gate': "The silence is completely silent: the microphone's noise suppression or a noise gate is on.",
      'too-short': 'One of the sounds was too short. Hold it for the whole bar.',
      's-sh-close': 's and š sounded too alike. Round your lips for š and move the tongue back.',
      'z-not-voiced': 'The z did not buzz. Let the vocal folds vibrate.',
    },
    keepAnyway: 'Save anyway',
    micFirst: 'Open the microphone first.',
  },
  voice: {
    ladder: 'Ladder',
    free: 'Free',
    freeHint: 'Say any sentence, English too. Pitch is a measurement, not a grade.',
    measure: {
      range: (st) => `range ${enNum(st, 1)} semitones`,
      voiced: (s) => `voiced ${enNum(s, 1)} s`,
      lift: (st) => `lift on the stressed syllable ${st >= 0 ? '+' : ''}${enNum(st, 1)}`,
      loud: (db) => `stressed syllable ${db >= 0 ? '+' : ''}${enNum(db, 1)} dB`,
      span: (db) => `loudness varied ${enNum(db)} dB`,
    },
    rangeLabel: 'Range',
    yourLast: 'Last take',
    none: 'Say something and the pitch line draws.',
  },
  diag: {
    open: 'Open the microphone',
    close: 'Close the microphone',
    now: 'The microphone now',
    device: 'Device',
    rate: 'Sample rate',
    processing: 'Voice processing',
    raw: 'off',
    on: (what) => `on: ${what}`,
    ec: 'echo cancelling',
    ns: 'noise suppression',
    agc: 'automatic gain',
    band: 'Hears',
    bandHz: (hz) => `up to ${enNum(hz)} Hz`,
    floor: 'Background noise',
    floorDb: (db) => `${enNum(db)} dB`,
    hop: 'Delay',
    hopMs: (ms) => `${enNum(ms, 1)} ms`,
    waiting: 'measuring…',
    check: {
      h: 'Check that voice processing is off',
      start: 'Start the check',
      again: 'Check again',
      steps: {
        quiet: 'Be quiet for 3 seconds.',
        normal: 'Read in your normal voice: «valar morghūlis».',
        loud: 'Read the same louder.',
        hiss: 'Say a long s.',
      },
      ok: 'The microphone gives unprocessed sound.',
      found: {
        flags: (what) => `The browser says ${what} is on.`,
        gate: 'The silence between words is completely silent: a noise gate or noise suppression is on.',
        compressor: 'The level did not grow when you spoke louder: a compressor or automatic gain is on.',
        deesser: 'The s hiss is weak: a de-esser or a band limit is on.',
        band: (hz) => `The microphone hears only up to ${enNum(hz)} Hz: the hiss's place is not measured, everything else works.`,
        noise: 'The background noise is loud.',
      },
    },
    pc: {
      h: 'Microphone on a PC',
      lead: "The coach needs the microphone's raw sound. Turn these off so the meters see the hiss and the voice as they are.",
      steps: [
        'Logitech G HUB: pick the G Pro X and turn Blue VO!CE off. That turns off noise reduction, the compressor, the de-esser, the limiter and the noise gate together.',
        'Windows 11: Settings → System → Sound → Input → pick the microphone. Set Audio enhancements to Off. That also turns off Windows Voice Clarity.',
        'If the PC has Windows Studio Effects: Settings → Bluetooth & devices → Cameras → pick the camera and turn Voice focus off. The Studio effects tile in Quick Settings does the same.',
        'In the same view: volume about 70–80 without boost, format 48,000 Hz.',
        'Use Chrome or Edge.',
        'Close Discord, NVIDIA Broadcast and Krisp, or turn their voice processing off.',
      ],
      after: 'Then check below that no processing was left on.',
    },
    playback: {
      h: 'Playback with the microphone open',
      ask: "Did Peterson's recording sound the same with the microphone open?",
      play: 'Play a sample',
      same: 'The same',
      worse: 'Worse',
      unsure: 'Cannot tell',
    },
  },
  credits: {
    model: 'Pronunciation recognition',
    modelText: 'ZIPA-CR-NS-small, licence CC BY-NC-SA 4.0. The model runs on the phone; no audio is sent anywhere.',
    lips: 'Lips camera',
    lipsText: 'MediaPipe Face Landmarker, licence Apache 2.0. The camera picture is processed on the phone and never stored.',
    peterson: 'Model voices',
    petersonText: "Every High Valyrian model voice is David J. Peterson's own recording from his wiki.",
  },
};

// Messages.coachUi (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('coachUi', coachUiFi, coachUiEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof coachUiFi> {}
}
